import { action, persist, thunk } from 'easy-peasy';
import type { Action, State, Thunk } from 'easy-peasy';

export type ViewLocation = 'tab' | 'window';

/**
 * One opened thing in a route's workspace. `id` is the consumer's entity id
 * (e.g. an assignment id); it doubles as the tab id and, for consumers that
 * render windows, the window-entity id.
 */
export interface OpenView {
    id: string;
    title: string;
    location: ViewLocation;
    /**
     * Pinned views are the route's primary surface: they cannot be closed and
     * are anchored at the start of the tab strip. Consumers seed at most one.
     */
    pinned?: boolean;
}

/** Per-route workspace: what is open, in what order, and which surface is focused. */
export interface RouteWorkspace {
    /** Open views keyed by view id. */
    views: Record<string, OpenView>;
    /** View-id order in the tab strip. */
    order: string[];
    /** Focused surface: a view id, or `'main'` for the route's own primary view. */
    activeId: string | 'main';
}

interface OpenPayload {
    scopeKey: string;
    view: Omit<OpenView, 'location'>;
}

interface ViewPayload {
    scopeKey: string;
    viewId: string;
}

interface ReplacePayload {
    scopeKey: string;
    viewId: string;
    view: Omit<OpenView, 'location'>;
}

/**
 * Generic, cross-navigation UI-state slice: records, per route scope, which
 * entities the user has opened as tabs or windows, their order, and the focused
 * surface. Domain-agnostic (no assignment or window-library types) so any page
 * can reuse it. Persisted to `sessionStorage` (see the model export): the open
 * set, order and focus survive a page reload within the same browser-tab
 * session, while a fresh session starts empty. In-app navigation away still
 * clears a scope via `resetScope` (an unmount cleanup, which a reload does not
 * run), so only a reload preserves it. `location` is just `'tab' | 'window'`;
 * how a window is actually rendered is the consumer's concern.
 *
 * Scope keys combine the static route id and its identifying param, e.g.
 * `examPassDetailsAssignments:42`, so several routes (or several params on the
 * same route) keep independent workspaces.
 */
/**
 * Store injections this model depends on. `nativeWindowManager.reserve` opens a
 * native popup *synchronously*; it is injected rather than imported so this
 * generic model stays free of a UI-library dependency (wired in `store.ts`).
 */
export interface WorkspaceInjections {
    nativeWindowManager: { reserve: (id: string) => void };
}

export interface WorkspaceModel {
    routes: Record<string, RouteWorkspace>;

    /** Open a view as a tab (or refocus it if already open). */
    openInTab: Action<WorkspaceModel, OpenPayload>;
    /**
     * Open a view as a window (or refocus it if already open). A thunk, not a
     * plain action: it opens the native popup synchronously inside the
     * dispatching click so the browser keeps the user gesture and doesn't block
     * it (a no-op in virtual mode), then applies the placement. Consumers just
     * dispatch this — the popup mechanics stay hidden.
     */
    openInWindow: Thunk<WorkspaceModel, OpenPayload, WorkspaceInjections>;
    /** Move an open view into the tab strip. */
    moveToTab: Action<WorkspaceModel, ViewPayload>;
    /** Move an open view into a window. A thunk, for the same in-gesture reason as {@link openInWindow}. */
    moveToWindow: Thunk<WorkspaceModel, ViewPayload, WorkspaceInjections>;
    /** @internal State change behind {@link openInWindow}; do not call directly. */
    applyOpenInWindow: Action<WorkspaceModel, OpenPayload>;
    /** @internal State change behind {@link moveToWindow}; do not call directly. */
    applyMoveToWindow: Action<WorkspaceModel, ViewPayload>;
    /** Close an open view; if it was focused, focus the adjacent view else `'main'`. */
    dismiss: Action<WorkspaceModel, ViewPayload>;
    /**
     * Move the primary (`pinned`) flag onto `viewId`, clearing it from any
     * other view. No-op if the view is not open. Lets a consumer transfer the
     * non-closable primary surface to another open view — e.g. when the
     * current primary is externalized into a tab or window and a neighbour
     * takes over as the route's primary.
     */
    promotePrimary: Action<WorkspaceModel, ViewPayload>;
    /** Focus an open view. */
    focus: Action<WorkspaceModel, ViewPayload>;
    /** Focus the route's primary (`'main'`) surface. */
    focusMain: Action<WorkspaceModel, { scopeKey: string }>;
    /** Replace the tab-strip order. */
    reorder: Action<WorkspaceModel, { scopeKey: string; order: string[] }>;
    /**
     * Re-point a view in place: drop `viewId` and put `view` into its order
     * slot, then focus it. Used to navigate the current tab to another view
     * without it jumping to the end of the strip. If `view` is already open,
     * its position is left untouched and it is just focused.
     */
    replaceView: Action<WorkspaceModel, ReplacePayload>;
    /** Drop a whole scope; consumers call this on unmount. */
    resetScope: Action<WorkspaceModel, { scopeKey: string }>;
}

/**
 * `persist` keeps only the serializable `routes` slice in `sessionStorage`: the
 * open set, order and focus restore on a page reload but never leak into a new
 * browser-tab session. Rehydration is synchronous, so the first render already
 * sees the restored workspace.
 */
export const workspaceModel: WorkspaceModel = persist(
    {
        routes: {},

        openInTab: action((state, payload) => open(state, payload, 'tab')),
        applyOpenInWindow: action((state, payload) =>
            open(state, payload, 'window'),
        ),
        openInWindow: thunk((actions, payload, { injections }) => {
            // Open the popup now, in the dispatching click's call stack, so the
            // browser keeps the user gesture (no-op in virtual mode); the
            // renderer adopts it. Then apply the placement.
            injections.nativeWindowManager.reserve(payload.view.id);
            actions.applyOpenInWindow(payload);
        }),

        moveToTab: action((state, { scopeKey, viewId }) => {
            const workspace = state.routes[scopeKey];
            const view = workspace?.views[viewId];
            if (view) {
                // Docking a window back into the strip focuses it: it re-enters
                // the tab world, so it becomes the active tab (and the URL the
                // consumer mirrors from it).
                view.location = 'tab';
                workspace.activeId = viewId;
            }
        }),
        applyMoveToWindow: action((state, { scopeKey, viewId }) => {
            const workspace = state.routes[scopeKey];
            const view = workspace?.views[viewId];
            // A pinned view is the route's primary surface and never leaves the
            // strip, so there is always at least one tab; ignore the request.
            if (!view || view.pinned) {
                return;
            }
            view.location = 'window';
            // Windows are non-focal: if the moved view was the active tab,
            // focus falls to a neighbouring tab so `activeId` never points at a
            // window.
            if (workspace.activeId === viewId) {
                workspace.activeId = nearestTab(
                    workspace,
                    workspace.order.indexOf(viewId),
                    viewId,
                );
            }
        }),
        moveToWindow: thunk((actions, { scopeKey, viewId }, { injections }) => {
            // Reserve the popup in-gesture (see `openInWindow`), then apply.
            injections.nativeWindowManager.reserve(viewId);
            actions.applyMoveToWindow({ scopeKey, viewId });
        }),

        promotePrimary: action((state, { scopeKey, viewId }) => {
            const workspace = state.routes[scopeKey];
            const target = workspace?.views[viewId];
            if (!target) {
                return;
            }
            // Exactly one view is the primary: clear the flag everywhere else,
            // then set it on the target. The tab strip anchors the pinned view
            // to the start from `pinned` alone, so no reorder is needed here.
            for (const id of Object.keys(workspace.views)) {
                if (id !== viewId && workspace.views[id].pinned) {
                    delete workspace.views[id].pinned;
                }
            }
            target.pinned = true;
        }),

        focus: action((state, { scopeKey, viewId }) => {
            const workspace = state.routes[scopeKey];
            if (workspace?.views[viewId]) {
                workspace.activeId = viewId;
            }
        }),
        focusMain: action((state, { scopeKey }) => {
            const workspace = state.routes[scopeKey];
            if (workspace) {
                workspace.activeId = 'main';
            }
        }),

        reorder: action((state, { scopeKey, order }) => {
            const workspace = state.routes[scopeKey];
            if (workspace) {
                workspace.order = order;
            }
        }),

        replaceView: action((state, { scopeKey, viewId, view }) => {
            const workspace = state.routes[scopeKey];
            if (!workspace) {
                return;
            }
            const index = workspace.order.indexOf(viewId);
            const replaced = workspace.views[viewId];
            if (replaced) {
                delete workspace.views[viewId];
                if (index >= 0) {
                    workspace.order.splice(index, 1);
                }
            }
            // Land the incoming view in the outgoing view's slot; if it is already
            // open elsewhere, leave its position untouched and just focus it.
            if (!workspace.views[view.id]) {
                // Inherit pinning from the replaced view so navigating the primary
                // tab in place keeps it pinned (and anchored at the start).
                const pinned = view.pinned ?? replaced?.pinned;
                workspace.views[view.id] = {
                    ...view,
                    location: replaced?.location ?? 'tab',
                    ...(pinned ? { pinned: true } : {}),
                };
                const insertAt = index >= 0 ? index : workspace.order.length;
                workspace.order.splice(insertAt, 0, view.id);
            }
            workspace.activeId = view.id;
        }),

        resetScope: action((state, { scopeKey }) => {
            delete state.routes[scopeKey];
        }),

        dismiss: action((state, { scopeKey, viewId }) => {
            const workspace = state.routes[scopeKey];
            if (!workspace?.views[viewId]) {
                return;
            }
            const index = workspace.order.indexOf(viewId);
            delete workspace.views[viewId];
            workspace.order.splice(index, 1);
            if (workspace.activeId === viewId) {
                // Focus the nearest remaining tab (skipping windows, which are
                // never the active surface), else main.
                workspace.activeId = nearestTab(workspace, index, undefined);
            }
        }),
    },
    {
        storage: 'sessionStorage',
        allow: ['routes'],
    },
);

export default workspaceModel;

// Hoisted helpers behind the actions above, kept below the public model so the
// store's shape and actions read first; function hoisting keeps them callable
// from the action closures regardless of source order.

type WorkspaceState = State<WorkspaceModel>;

function ensureScope(state: WorkspaceState, scopeKey: string): RouteWorkspace {
    if (!state.routes[scopeKey]) {
        state.routes[scopeKey] = { views: {}, order: [], activeId: 'main' };
    }
    return state.routes[scopeKey];
}

function open(
    state: WorkspaceState,
    { scopeKey, view }: OpenPayload,
    location: ViewLocation,
): void {
    const workspace = ensureScope(state, scopeKey);
    const existing = workspace.views[view.id];
    if (existing) {
        // Already open — never duplicate; just re-place it. A pinned primary
        // stays in the strip, so refuse to flip it into a window.
        if (location === 'window' && existing.pinned) {
            return;
        }
        existing.location = location;
    } else {
        workspace.views[view.id] = { ...view, location };
        workspace.order.push(view.id);
    }
    if (location === 'tab') {
        // Tabs are focal — opening one focuses it.
        workspace.activeId = view.id;
    } else if (workspace.activeId === view.id) {
        // Placing the active view into a window is non-focal: focus falls to a
        // neighbouring tab so `activeId` never points at a window.
        workspace.activeId = nearestTab(
            workspace,
            workspace.order.indexOf(view.id),
            view.id,
        );
    }
}

/**
 * Nearest focusable tab to `fromIndex` in `order`: search forward from the slot,
 * then backward, skipping windowed views (never the active surface) and
 * `excludeId`. Returns `undefined` when no tab remains.
 *
 * Exported so a consumer that mirrors the active tab elsewhere (e.g. into the
 * URL) reuses the exact same neighbour pick the store lands on, instead of
 * re-deriving it and risking drift from `activeId`.
 */
export function nearestTabId(
    order: string[],
    views: Record<string, OpenView>,
    fromIndex: number,
    excludeId?: string,
): string | undefined {
    const focusable = (id: string | undefined): id is string =>
        id !== undefined && id !== excludeId && views[id]?.location === 'tab';
    for (let i = fromIndex; i < order.length; i++) {
        if (focusable(order[i])) {
            return order[i];
        }
    }
    for (let i = fromIndex - 1; i >= 0; i--) {
        if (focusable(order[i])) {
            return order[i];
        }
    }
    return undefined;
}

/**
 * Store-internal wrapper around {@link nearestTabId} that falls back to `'main'`
 * (the route's primary surface) when no tab remains, so `activeId` always
 * resolves to a real focus target.
 */
function nearestTab(
    workspace: RouteWorkspace,
    fromIndex: number,
    excludeId: string | undefined,
): string {
    return (
        nearestTabId(workspace.order, workspace.views, fromIndex, excludeId) ??
        'main'
    );
}
